import { get } from 'lodash';
import { isDefined, isEmpty } from 'class-validator';
import { CustomFormDataEntitiesEnum } from '@nova/core';
import customFormsService from '@satellite/services/custom-forms-service.js';
import assetVisitService from '@satellite/services/asset-visit-service.js';

export default {
  name: 'customFormsDataMixin',
  props: {
    objectIds: {
      type: Object,
      required: true,
      validator(value) {
        const allowedKeys = ['appointmentId', 'assetVisitId'];

        return (
          typeof value === 'object' &&
          value !== null &&
          allowedKeys.some(key => Object.prototype.hasOwnProperty.call(value, key))
        );
      }
    },
    trigger: {
      type: Object,
      required: true
    },
    timezone: {
      type: String,
      required: true
    },
    militaryTimeEnabled: {
      type: Boolean,
      default: false
    },
    prefetchedCustomFormData: {
      type: Object,
      required: false,
      default: null
    },
    prependedData: {
      type: Array,
      required: false,
      default() {
        return [];
      }
    }
  },
  data() {
    return {
      customFormData: [],
      fetchedAssetVisitId: null,
      loading: false,
      abortController: null
    };
  },
  computed: {
    hasCustomFormData() {
      return this.customFormData?.length > 0;
    }
  },
  methods: {
    async ensureRequiredData() {
      const { id: triggerId, dataEntityName } = this.trigger ?? {};
      const appointmentId = get(this.objectIds, 'appointmentId');
      let assetVisitId = get(this.objectIds, 'assetVisitId') || this.fetchedAssetVisitId;

      if (!triggerId || !dataEntityName) {
        throw new Error('No triggerId or dataEntityName found were provided to custom forms data');
      }

      if (dataEntityName === 'appointment' && !appointmentId) {
        throw new Error(
          'No appointmentId was provided to fetch custom forms data for',
          JSON.stringify({
            triggerId,
            dataEntityName
          })
        );
      }

      const isAssetVisitTrigger = dataEntityName === CustomFormDataEntitiesEnum.assetVisit;
      const isAppointmentTrigger = dataEntityName === CustomFormDataEntitiesEnum.appointment;

      // if we don't have an assetVisitId, we need an appointmentId to fetch the asset visit
      if (isAssetVisitTrigger && !assetVisitId && !appointmentId) {
        throw new Error(
          'No appointmentId was provided to fetch asset visit for',
          JSON.stringify({
            triggerId,
            dataEntityName
          })
        );
      }

      /**
       * TODO: should we control if we have attempted to fetch the asset visit before?
       */
      if (isAssetVisitTrigger && !assetVisitId) {
        try {
          const assetVisit = await assetVisitService.getAssetVisitByAppointmentId(appointmentId, {
            requestConfig: {
              signal: this.abortController?.signal
            }
          });

          this.fetchedAssetVisitId = assetVisit?.id ?? null;
          assetVisitId = this.fetchedAssetVisitId;
        } catch (error) {
          throw new Error(
            'Error fetching asset visit for',
            JSON.stringify({
              appointmentId,
              triggerId,
              dataEntityName
            }),
            error
          );
        }
      }

      if (isAppointmentTrigger && !appointmentId) {
        throw new Error(
          'No appointmentId found for',
          JSON.stringify({
            triggerId,
            dataEntityName,
            appointmentId,
            assetVisitId
          })
        );
      }

      return {
        triggerId,
        dataEntityName,
        appointmentId,
        assetVisitId
      };
    },

    async getCustomFormData() {
      let appointmentId;
      let assetVisitId;
      let triggerId;
      let dataEntityName;

      this.loading = true;

      if (this.abortController) {
        this.abortController.abort();
      }

      this.abortController = new AbortController();

      try {
        ({ appointmentId, assetVisitId, triggerId, dataEntityName } =
          await this.ensureRequiredData());
      } catch (error) {
        this.loading = false;
        this.abortController = null;
        this.customFormData = [];

        console.error(error);
        return;
      }

      try {
        const mapDataEntityNameToId = {
          appointment: appointmentId,
          assetvisit: assetVisitId
        };
        const objectId = mapDataEntityNameToId[dataEntityName];

        if (triggerId && objectId) {
          const data = await customFormsService.getCustomFormData(
            {
              s: { objectId, triggerId },
              limit: 1000
            },
            {},
            {
              signal: this.abortController?.signal
            }
          );
          this.customFormData = this.processCustomFormData(data);
        }
      } catch (error) {
        this.customFormData = [];
        console.error(
          'Error fetching custom form data for',
          JSON.stringify({
            triggerId,
            dataEntityName,
            appointmentId,
            assetVisitId
          }),
          error
        );
      } finally {
        this.loading = false;
        this.abortController = null;
      }
    },
    processCustomFormData(data) {
      if (!data || !Array.isArray(data)) {
        return [];
      }

      const processedData = [...[...(this.prependedData || [])].reverse(), ...data];
      processedData.forEach(data => {
        /**
         * TODO: this is the same as as what happens in useCustomForms.js;
         * we should refactor this to use the same function
         */
        data.label = data.formField?.overrideLabel || data.label;
        data.value = this.formattedValue(data);
      });

      return processedData;
    },
    formattedValue(field) {
      if (
        field.value === '' ||
        !isDefined(field.value) ||
        isEmpty(field.value) ||
        (Array.isArray(field.value) && field.value.length === 0)
      ) {
        return '--';
      }
      return this.novaCore.getCustomFieldFormattedValue(field, {
        [this.novaCore.CustomFieldType.Document]: { generateLink: true },
        [this.novaCore.CustomFieldType.MultiDocument]: { generateLink: true },
        [this.novaCore.CustomFieldType.Timestamp]: {
          timezone: this.timezone,
          formatAsMilitary: this.militaryTimeEnabled
        }
      });
    },
    getFormattedDate(date) {
      return this.novaCore.formatDateTimeWithMilitarySupport(
        date,
        this.timezone,
        this.novaCore.LuxonDateTimeFormats.DateSlashedDashTimestamp12HourTimeAMPM,
        this.militaryTimeEnabled,
        this.novaCore.LuxonDateTimeFormats.DateSlashedDashTimestamp24HourTime
      );
    }
  }
};
